import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Skeleton } from "../components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Save, Sliders, Coins, Banknote, LineChart, MapPinned, ShieldAlert, Bell, Radio } from "lucide-react";
import { toast } from "sonner";

// Tab metadata — order matches the spec.
const TABS = [
    { key: "scoring", label: "Scoring", icon: Sliders },
    { key: "renovation", label: "Renovation", icon: Coins },
    { key: "tx_costs", label: "Tx costs", icon: Banknote },
    { key: "market", label: "Market assumptions", icon: LineChart },
    { key: "zones", label: "Zones", icon: MapPinned },
    { key: "data_quality", label: "Data quality", icon: ShieldAlert },
    { key: "alerts", label: "Alerts", icon: Bell },
    { key: "sources", label: "Sources", icon: Radio },
];

const Settings = () => {
    const [s, setS] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/settings");
            setS(data);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const save = async () => {
        setSaving(true);
        try {
            await api.put("/settings", s);
            toast.success("Settings saved");
        } catch (e) {
            toast.error("Save failed", { description: e?.response?.data?.detail || String(e) });
        } finally {
            setSaving(false);
        }
    };

    const setPath = (path, value) => {
        setS((prev) => {
            const next = JSON.parse(JSON.stringify(prev));
            let cur = next;
            const parts = path.split(".");
            for (let i = 0; i < parts.length - 1; i++) cur = cur[parts[i]];
            cur[parts[parts.length - 1]] = value;
            return next;
        });
    };

    if (loading || !s) return <Skeleton className="h-96 w-full" />;

    return (
        <div className="space-y-6" data-testid="settings-page">
            <header className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-3xl sm:text-4xl font-serif">Settings</h1>
                    <p className="text-sm text-muted-foreground mt-1 max-w-xl">
                        The underwriting control room. Every value drives the scoring,
                        market intelligence, financial scenarios, data-quality flags,
                        and alerts modules.
                    </p>
                </div>
                <Button onClick={save} disabled={saving} data-testid="save-settings-btn">
                    <Save className="w-4 h-4 mr-2" />{saving ? "Saving…" : "Save"}
                </Button>
            </header>

            <Tabs defaultValue="scoring">
                <TabsList className="flex flex-wrap h-auto">
                    {TABS.map((t) => {
                        const Icon = t.icon;
                        return <TabsTrigger key={t.key} value={t.key} className="flex items-center gap-1.5" data-testid={`settings-tab-${t.key}`}>
                            <Icon className="w-3.5 h-3.5" /> {t.label}
                        </TabsTrigger>;
                    })}
                </TabsList>

                <TabsContent value="scoring" className="mt-5">
                    <Card className="p-5 space-y-4">
                        <SectionHeader title="Scoring weights" hint="Relative importance of each scoring factor. Higher → more weight in flip_score." />
                        <Grid cols={2}>
                            {Object.entries(s.weights).map(([k, v]) => (
                                <NumField key={k} label={k} value={v} onChange={(val) => setPath(`weights.${k}`, val)} testid={`w-${k}`} />
                            ))}
                        </Grid>
                    </Card>
                </TabsContent>

                <TabsContent value="renovation" className="mt-5">
                    <Card className="p-5 space-y-4">
                        <SectionHeader title="Renovation budgets (€/m²)" hint="Used by the Financial scenarios. Higher → more conservative." />
                        <Grid cols={2}>
                            {Object.entries(s.renovation_budgets).map(([k, v]) => (
                                <NumField key={k} label={k} value={v} onChange={(val) => setPath(`renovation_budgets.${k}`, val)} />
                            ))}
                        </Grid>
                    </Card>
                </TabsContent>

                <TabsContent value="tx_costs" className="mt-5">
                    <Card className="p-5 space-y-4">
                        <SectionHeader title="Transaction costs" hint="Applied on both buy and sell sides of the underwriting." />
                        <Grid cols={2}>
                            {Object.entries(s.tx_costs).map(([k, v]) => (
                                <NumField key={k} label={k} value={v} onChange={(val) => setPath(`tx_costs.${k}`, val)} />
                            ))}
                        </Grid>
                    </Card>
                </TabsContent>

                <TabsContent value="market" className="mt-5">
                    <Card className="p-5 space-y-4">
                        <SectionHeader title="Market assumptions" hint="Drive Conservative / Base / Upside scenario discounts + premiums + required margins." />
                        <Grid cols={2}>
                            {Object.entries(s.market_assumptions || {}).map(([k, v]) => (
                                <NumField key={k} label={k} value={v} onChange={(val) => setPath(`market_assumptions.${k}`, val)} testid={`ma-${k}`} />
                            ))}
                        </Grid>
                    </Card>
                </TabsContent>

                <TabsContent value="zones" className="mt-5 space-y-3">
                    <Card className="p-5">
                        <SectionHeader title="Preferred zones" hint="Coastal towns and neighbourhoods you actively want flips in." />
                        <Textarea
                            rows={3}
                            value={(s.preferred_zones || []).join(", ")}
                            onChange={(e) => setPath("preferred_zones", e.target.value.split(",").map((v) => v.trim()).filter(Boolean))}
                            placeholder="Tivat, Porto Montenegro, Budva, Kotor, ..."
                            data-testid="zones-preferred"
                        />
                    </Card>
                    <Card className="p-5">
                        <SectionHeader title="Speculative zones" hint="Tolerated but not preferred. Listings here need stronger numbers to qualify." />
                        <Textarea
                            rows={3}
                            value={(s.speculative_zones || []).join(", ")}
                            onChange={(e) => setPath("speculative_zones", e.target.value.split(",").map((v) => v.trim()).filter(Boolean))}
                            placeholder="Bar, Dobre Vode, Utjeha, Sutomore, Ulcinj, Risan, Bigova"
                            data-testid="zones-speculative"
                        />
                    </Card>
                    <Card className="p-5">
                        <SectionHeader title="Avoid zones" hint="Hard-no. Listings here get the lowest score weight." />
                        <Textarea
                            rows={3}
                            value={(s.avoid_zones || []).join(", ")}
                            onChange={(e) => setPath("avoid_zones", e.target.value.split(",").map((v) => v.trim()).filter(Boolean))}
                            placeholder="Podgorica, Nikšić, inland locations without buyer demand"
                            data-testid="zones-avoid"
                        />
                    </Card>
                </TabsContent>

                <TabsContent value="data_quality" className="mt-5">
                    <Card className="p-5 space-y-4">
                        <SectionHeader title="Data-quality thresholds" hint="Tune what counts as a price/m² outlier. Changes take effect on the next /quality/backfill call." />
                        <Grid cols={2}>
                            {Object.entries(s.data_quality_settings || {}).map(([k, v]) => (
                                typeof v === "boolean"
                                    ? <ToggleField key={k} label={k} value={v} onChange={(val) => setPath(`data_quality_settings.${k}`, val)} />
                                    : <NumField key={k} label={k} value={v} onChange={(val) => setPath(`data_quality_settings.${k}`, val)} testid={`dq-${k}`} />
                            ))}
                        </Grid>
                    </Card>
                </TabsContent>

                <TabsContent value="alerts" className="mt-5">
                    <Card className="p-5 space-y-4">
                        <SectionHeader title="Alert triggers" hint="Each alert evaluates against REAL listings only. Run /alerts/evaluate after editing thresholds." />
                        <Grid cols={2}>
                            {Object.entries(s.alert_settings || {}).map(([k, v]) => (
                                typeof v === "boolean"
                                    ? <ToggleField key={k} label={k} value={v} onChange={(val) => setPath(`alert_settings.${k}`, val)} testid={`a-${k}`} />
                                    : <NumField key={k} label={k} value={v} onChange={(val) => setPath(`alert_settings.${k}`, val)} testid={`a-${k}`} />
                            ))}
                        </Grid>
                        <div className="pt-3 border-t border-border/40">
                            <Button variant="outline" size="sm" onClick={async () => {
                                try {
                                    const { data } = await api.post("/alerts/evaluate?limit=2000");
                                    toast.success(`Re-evaluated alerts`, { description: `${data.created} new alert(s) created from real listings` });
                                } catch (e) {
                                    toast.error("Evaluate failed", { description: String(e) });
                                }
                            }} data-testid="evaluate-alerts-btn">
                                Re-evaluate alerts now
                            </Button>
                        </div>
                    </Card>
                </TabsContent>

                <TabsContent value="sources" className="mt-5">
                    <Card className="p-5">
                        <SectionHeader title="Scrapers & sources" hint="Manage scrapers, parser status and triage. The Sources page has the full registry — this tab links to it." />
                        <Button asChild variant="outline" size="sm">
                            <a href="/sources">Open Sources page →</a>
                        </Button>
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    );
};

const SectionHeader = ({ title, hint }) => (
    <div>
        <h2 className="font-grotesk font-semibold text-base">{title}</h2>
        {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
    </div>
);

const Grid = ({ cols = 2, children }) => (
    <div className={`grid grid-cols-1 sm:grid-cols-${cols} gap-3`}>{children}</div>
);

const NumField = ({ label, value, onChange, testid }) => (
    <div>
        <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</Label>
        <Input
            type="number"
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
            className="mt-1"
            data-testid={testid}
        />
    </div>
);

const ToggleField = ({ label, value, onChange, testid }) => (
    <div className="flex items-center justify-between py-2 px-3 border border-border/40 rounded-md">
        <Label className="text-xs">{label}</Label>
        <Switch checked={!!value} onCheckedChange={onChange} data-testid={testid} />
    </div>
);

export default Settings;
